import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import { useRecoilCallback, useRecoilState } from 'recoil'
import { zones } from 'Helpers/dates'

import {
  FormElement,
  DatePicker,
  Select,
  InputNumber,
  MessageBox,
  Text,
  MESSAGE_BOX_VARIANT_DANGER,
  Flex,
  FlexItem
} from 'mailup-web-components'
import styled from '@emotion/styled'

import {
  contentScheduledState,
  INPUT_MINUTES_STEP,
  MAX_H,
  MAX_M,
  MIN_H,
  MIN_M,
  TYPE_D,
  TYPE_H,
  TYPE_M
} from './recoil/state'
import { initDateCallback, setDateCallback } from './recoil/callbacks'
import { MODE_SMART } from 'Constants/index'

const StyledSpan = styled.span`
  position: relative;
  top: 8px;
`

const SendTypeScheduledText = () => {
  const intl = useIntl()
  return (
    <Text>
      {intl.formatMessage(
        {
          id: 'Load.Schedulation.SendTypeScheduledText'
        },
        {
          strong: chunks => <strong key='1'>{chunks}</strong>
        }
      )}
    </Text>
  )
}

const DangerMessageBox = ({ closeButtonCallback }) => {
  const intl = useIntl()
  return (
    <MessageBox
      variant={MESSAGE_BOX_VARIANT_DANGER}
      closeButton
      closeButtonCallback={closeButtonCallback}
      data-mu-test='message-box-error'
    >
      <>
        {intl.formatMessage({
          id: 'Load.Common.ValidationScheduledDateTime'
        })}
      </>
    </MessageBox>
  )
}

export const ContentScheduled = ({
  scheduleDate,
  scheduleTimeZoneCode,
  dateDisabled,
  hoursDisabled,
  minutesDisabled,
  timeZoneDisabled,
  minDate,
  sendTypeScheduledTextVisible,
  oneRow,
  description,
  mode
}) => {
  const intl = useIntl()

  const [selectTimezone, setSelectTimezone] = useState('')

  const [dateModel, setDateModel] = useRecoilState(contentScheduledState)
  const initDate = useRecoilCallback(initDateCallback, [])
  const setDate = useRecoilCallback(setDateCallback, [])

  useEffect(() => {
    initDate(scheduleDate, scheduleTimeZoneCode)
  }, [scheduleDate])
  const handleChangeDate = jsDate => {
    if (jsDate instanceof Date && !isNaN(jsDate)) {
      setDate(TYPE_D, jsDate)
    }
  }

  useEffect(() => {
    if (selectTimezone !== '') {
      // nuova utc date in base alla zona vecchia
      const m = dateModel.momentUtcDate.clone()
      setDate(TYPE_D, m, selectTimezone)
    }
  }, [selectTimezone])

  useEffect(() => {
    if (mode === MODE_SMART) {
      initDate(scheduleDate, scheduleTimeZoneCode)
    }
  }, [mode])

  const handleChangeTimeZone = value => {
    // la select mantiene i dati "immutabili"
    setSelectTimezone(value)
    setDateModel(prev => ({
      ...prev,
      isDirty: true
    }))
  }

  const handleChangeHours = value => {
    if (typeof value !== 'number' || value === '' || value > 23 || value < 0) {
      setDateModel(prev => ({
        ...prev,
        validHour: false,
        showFeedback: false,
        isDirty: true
      }))
    } else {
      setDate(TYPE_H, value)
    }
  }

  const handleChangeMinutes = value => {
    if (typeof value !== 'number' || value === '' || value >= 60 || value < 0) {
      setDateModel(prev => ({
        ...prev,
        validMinutes: false,
        showFeedback: false,
        isDirty: true
      }))
    } else {
      setDate(TYPE_M, value)
    }
  }

  const isValidMinutes = () => {
    return (
      typeof +dateModel.momentTimeZoneDate.format('HH') === 'number' &&
      dateModel.validMinutes
    )
  }

  const isValidHours = () => {
    return (
      typeof +dateModel.momentTimeZoneDate.format('mm') === 'number' &&
      dateModel.validHour
    )
  }

  const getFeedback = () => {
    let retStr = null
    if (dateModel.showFeedback) {
      retStr = intl.formatMessage({
        id: 'Load.Common.ValidationScheduledTime'
      })
    } else {
      if (!(isValidMinutes() && isValidHours())) {
        retStr = intl.formatMessage({
          id: 'Load.Common.InvalidTime'
        })
      }
    }

    return retStr
  }

  const getCurrentDate = () => {
    return new Date(
      new Date(dateModel.momentUtcDate.format()).toLocaleString('en-US', {
        timeZone: zones[dateModel.timeZone]
      })
    )
  }

  const getMinDate = () => {
    return new Date(
      new Date().toLocaleString('en-US', {
        timeZone: zones[dateModel.timeZone]
      })
    )
  }

  return (
    <>
      {dateModel.boxError && (
        <DangerMessageBox
          closeButtonCallback={() => {
            setDateModel(prev => ({
              ...prev,
              boxError: false
            }))
          }}
        />
      )}
      {sendTypeScheduledTextVisible && (
        <>{description || <SendTypeScheduledText />}</>
      )}
      <Flex
        justifyContent='space-between'
        flexWrap='wrap'
        gap={oneRow ? '16px' : ''}
      >
        <FlexItem
          flexBasis={oneRow ? 'calc(25% - 8px)' : 'calc(50% - 8px)'}
          margin='16px 0 0 0'
        >
          <FormElement
            label={intl.formatMessage({
              id: 'Load.Schedulation.setScheduleLblData'
            })}
            feedback={
              !dateModel.validDate
                ? intl.formatMessage({
                    id: 'Load.Common.ValidationScheduledDate'
                  })
                : null
            }
          >
            <StyledDatePicker
              value={getCurrentDate()}
              callback={handleChangeDate}
              minDate={getMinDate()}
              disabled={dateDisabled}
              invalid={!dateModel.validDate}
            />
          </FormElement>
        </FlexItem>
        <FlexItem
          flexBasis={oneRow ? 'calc(25% - 8px)' : 'calc(50% - 8px)'}
          margin='16px 0 0 0'
        >
          <FormElement
            feedback={getFeedback()}
            label={intl.formatMessage({
              id: 'Load.Schedulation.setScheduleLblOra'
            })}
          >
            <Flex justifyContent='space-between'>
              <FlexItem flexBasis='calc(49% - 8px)'>
                <InputNumber
                  defaultValue={10}
                  placeholder='HH'
                  value={+dateModel.momentTimeZoneDate.format('HH')}
                  onChange={handleChangeHours}
                  variant={isValidHours() ? '' : 'danger'}
                  min={MIN_H}
                  max={MAX_H}
                  disabled={hoursDisabled}
                  data-mu-test='input-number__hour'
                />
              </FlexItem>
              <FlexItem flexBasis='calc(2% - 8px)'>
                <StyledSpan>:</StyledSpan>
              </FlexItem>
              <FlexItem flexBasis='calc(49% - 8px)'>
                <InputNumber
                  step={INPUT_MINUTES_STEP}
                  placeholder='MM'
                  value={+dateModel.momentTimeZoneDate.format('mm')}
                  onChange={handleChangeMinutes}
                  variant={isValidMinutes() ? '' : 'danger'}
                  min={MIN_M}
                  max={MAX_M}
                  disabled={minutesDisabled}
                  data-mu-test='input-number__min'
                />
              </FlexItem>
            </Flex>
          </FormElement>
        </FlexItem>
        <FlexItem
          flexBasis={oneRow ? 'calc(48% - 8px)' : 'calc(50% - 8px)'}
          margin='16px 0 0 0'
          flexGrow={oneRow ? '0' : '1'}
        >
          <FormElement
            label={intl.formatMessage({
              id: 'Load.Schedulation.setScheduleLblFuso'
            })}
          >
            <Select
              value={dateModel.timeZone}
              onChange={handleChangeTimeZone}
              searchable
              disabled={timeZoneDisabled}
              options={Object.entries(zones).map(([code, label]) => ({
                value: code,
                label: `(${code.replace(/([^.]+)\.\d+$/, '$1')}) ${label}`
              }))}
            />
          </FormElement>
        </FlexItem>
      </Flex>
    </>
  )
}

ContentScheduled.defaultProps = {
  dateDisabled: false,
  hoursDisabled: false,
  minutesDisabled: false,
  timeZoneDisabled: false,
  minDate: new Date(),
  sendTypeScheduledTextVisible: true,
  oneRow: false
}

ContentScheduled.propTypes = {
  scheduleDate: PropTypes.any,
  scheduleTimeZoneCode: PropTypes.any,
  dateDisabled: PropTypes.bool,
  hoursDisabled: PropTypes.bool,
  minutesDisabled: PropTypes.bool,
  timeZoneDisabled: PropTypes.bool,
  minDate: PropTypes.Date,
  sendTypeScheduledTextVisible: PropTypes.bool,
  oneRow: PropTypes.bool,
  description: PropTypes.any,
  mode: PropTypes.string
}

DangerMessageBox.propTypes = {
  closeButtonCallback: PropTypes.func
}

const StyledDatePicker = styled(DatePicker)`
  width: 100%;
  .react-datepicker-wrapper {
    width: 100%;
  }
`
